import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ConversationAgentEntity } from './conversation-agent.entity';
import { ConversationSessionEntity } from './conversation-session.entity';

@Entity('_conversation_message')
@Index(['sessionId', 'createdDate'])
@Index(['sessionId', 'role'])
export class ConversationMessageEntity extends BaseEntity {
	@Column({
		name: 'fk_conversation_session__id',
		type: 'int',
		unsigned: true,
		nullable: false,
	})
	public sessionId!: number;

	@ManyToOne(() => ConversationSessionEntity, (session) => session.messages, {
		onDelete: 'CASCADE',
		onUpdate: 'CASCADE',
	})
	@JoinColumn({
		name: 'fk_conversation_session__id',
		referencedColumnName: 'id',
	})
	public session!: ConversationSessionEntity;

	@Column({
		name: 'message_uuid',
		type: 'varchar',
		length: 36,
		nullable: false,
		unique: true,
		comment: 'Unique identifier for the message used for queue operations',
	})
	@Index()
	public messageUuid!: string;

	@Column({
		name: 'role',
		type: 'varchar',
		length: 20,
		nullable: false,
	})
	public role!: 'user' | 'assistant' | 'system';

	@Column({
		name: 'content',
		type: 'text',
		nullable: false,
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public content!: string;

	@Column({
		name: 'command',
		type: 'json',
		nullable: true,
		comment:
			'Tool invocation JSON (e.g., {"tool": "clientSearch", "arguments": {...}})',
	})
	public command?: any;

	@Column({
		name: 'conversational_response',
		type: 'text',
		nullable: true,
		comment: 'Spoken response only (no tool invocations)',
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public conversationalResponse?: string;

	@Column({
		name: 'fk__conversation_agent__id',
		type: 'int',
		unsigned: true,
		nullable: true,
		comment: 'The agent ID used for this message',
	})
	public agentId?: number;

	@ManyToOne(() => ConversationAgentEntity, { nullable: true })
	@JoinColumn({
		name: 'fk__conversation_agent__id',
		referencedColumnName: 'id',
	})
	public agent?: ConversationAgentEntity;

	@Column({
		name: 'llm_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'LLM provider used (claude, openai, etc.)',
	})
	public llmProvider?: string;

	@Column({
		name: 'llm_model',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'Specific LLM model used',
	})
	public llmModel?: string;

	@Column({
		name: 'voice_provider',
		type: 'varchar',
		length: 50,
		nullable: true,
		comment: 'Voice provider used (elevenlabs, etc.)',
	})
	public voiceProvider?: string;

	@Column({
		name: 'voice_model',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'Specific voice model/ID used',
	})
	public voiceModel?: string;

	@Column({
		name: 'llm_execution_time_ms',
		type: 'int',
		nullable: true,
		comment: 'Time taken for LLM processing in milliseconds',
	})
	public llmExecutionTimeMs?: number;

	@Column({
		name: 'voice_execution_time_ms',
		type: 'int',
		nullable: true,
		comment: 'Time taken for voice generation in milliseconds',
	})
	public voiceExecutionTimeMs?: number;

	@Column({
		name: 'llm_tokens',
		type: 'int',
		nullable: true,
		comment: 'Number of tokens used by LLM',
	})
	public llmTokens?: number;

	@Column({
		name: 'voice_tokens',
		type: 'int',
		nullable: true,
		comment: 'Number of tokens/characters for voice generation',
	})
	public voiceTokens?: number;

	/**
	 * The WHOLE prompt for both providers, cache reads and cache writes
	 * included (OpenAI prompt_tokens; Claude input_tokens + cache reads + cache
	 * writes since PD-16962), so the column compares across providers.
	 */
	@Column({
		name: 'prompt_tokens',
		type: 'int',
		nullable: true,
		comment: 'LLM input (prompt) tokens for this completion',
	})
	public promptTokens?: number;

	@Column({
		name: 'completion_tokens',
		type: 'int',
		nullable: true,
		comment: 'LLM output (completion) tokens for this completion',
	})
	public completionTokens?: number;

	@Column({
		name: 'cached_tokens',
		type: 'int',
		nullable: true,
		comment:
			'Prompt tokens served from the provider prompt cache (OpenAI prompt_tokens_details.cached_tokens) — cache-hit visibility for prefix-layout tuning',
	})
	public cachedTokens?: number;

	/**
	 * PD-16963: the part of `prompt_tokens` written to the prompt cache, which
	 * Anthropic bills above the input rate. Zero on OpenAI turns (OpenAI bills
	 * no cache write); NULL on messages recorded before this column existed.
	 */
	@Column({
		name: 'cache_creation_tokens',
		type: 'int',
		nullable: true,
		comment:
			'Prompt tokens written to the provider prompt cache (Claude cache_creation_input_tokens; 0 on OpenAI) — billed at the cache-write rate',
	})
	public cacheCreationTokens?: number;

	@Column({
		name: 'llm_cost',
		type: 'decimal',
		precision: 12,
		scale: 6,
		nullable: true,
		comment:
			'USD cost of this completion computed at save time from the code-side rate card (llm-pricing). Raw token counts are the source of truth — cost is recomputable if a rate was wrong.',
	})
	public llmCost?: number | null;

	@Column({
		name: 'stt_to_dispatch_ms',
		type: 'int',
		nullable: true,
		comment:
			'Latency from STT final transcript to LLM dispatch for the turn that produced this message',
	})
	public sttToDispatchMs?: number;

	@Column({
		name: 'llm_ttft_ms',
		type: 'int',
		nullable: true,
		comment: 'LLM time-to-first-token for this completion',
	})
	public llmTtftMs?: number;

	@Column({
		name: 'tts_first_byte_ms',
		type: 'int',
		nullable: true,
		comment:
			'Latency from first LLM content to first audio pushed to the caller for this message',
	})
	public ttsFirstByteMs?: number;

	@Column({
		name: 'external_voice_id',
		type: 'varchar',
		length: 255,
		nullable: true,
		comment: 'External voice provider session/request ID for diagnostics',
	})
	public externalVoiceId?: string;

	@Column({
		name: 'tool_error',
		type: 'text',
		nullable: true,
		comment: 'Error message if tool execution failed',
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public toolError?: string;

	@Column({
		name: 'tool_error_details',
		type: 'json',
		nullable: true,
		comment: 'Detailed error information including stack trace',
	})
	public toolErrorDetails?: any;

	@Column({
		name: 'turn_id',
		type: 'int',
		nullable: true,
		comment:
			'Turn ID from STT provider for tracking eager vs final transcripts',
	})
	public turnId?: number;

	@Column({
		name: 'is_eager',
		type: 'boolean',
		default: false,
		nullable: false,
		comment: 'Whether this message was from an eager (pre-final) transcript',
	})
	public isEager!: boolean;

	@Column({
		name: 'aborted',
		type: 'boolean',
		default: false,
		nullable: false,
		comment:
			'Whether this message was aborted (eager turn was resumed with different content)',
	})
	public aborted!: boolean;

	@Column({
		name: 'was_interrupted',
		type: 'boolean',
		default: false,
		nullable: false,
		comment: 'Whether this message was interrupted by user speech',
	})
	public wasInterrupted!: boolean;

	@Column({
		name: 'spoken_content',
		type: 'text',
		nullable: true,
		comment:
			'The portion of the response that was actually spoken before interruption (only populated if wasInterrupted is true)',
		charset: 'utf8mb4',
		collation: 'utf8mb4_0900_ai_ci',
	})
	public spokenContent?: string;
}
